/* SignalIntelligencePanel.jsx — Phase 6 of Mission Control Evolution.
 *
 * Operational signal management surface. Mounts inside the existing
 * NotificationCenter dropdown, ABOVE the raw notification list.
 *
 * Sections (top → bottom):
 *   • Morning Briefing card — calm executive daily summary
 *   • Focus Mode toggle — temporarily suppresses non-critical signals
 *   • Tier-grouped operational alerts (critical / intervention /
 *     informational), each with calm headline + evidence + primary
 *     action button
 *
 * The component renders nothing when the backend reports no groups
 * AND no briefing data → falls through to the existing raw list.
 *
 * Visual identity: existing gp-card / gp-pill tokens · calm violet/
 * amber/slate accents · no red alarm · no flashing.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, AlertOctagon, ShieldAlert, EyeOff, Eye,
  ChevronRight, Sunrise,
} from "lucide-react";
import { api } from "../../lib/api";

const TIER = {
  critical:      { label: "Critical",      fg: "#9F1239", bg: "rgba(244,63,94,0.08)",   dot: "#F43F5E", icon: ShieldAlert },
  intervention:  { label: "Intervention",  fg: "#92400E", bg: "rgba(245,158,11,0.10)",  dot: "#F59E0B", icon: AlertOctagon },
  informational: { label: "Informational", fg: "#475569", bg: "rgba(148,163,184,0.10)", dot: "#94A3B8", icon: Sparkles },
};

const fmtGBP = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `£${Math.round(v / 1_000)}K`;
  return `£${Math.round(v).toLocaleString()}`;
};

/* ═══════════════════════════════════════════════════════════════════
   Morning Briefing
   ─────────────────────────────────────────────────────────────────── */
const MorningBriefing = ({ briefing }) => {
  if (!briefing?.headline) return null;
  return (
    <section
      className="rounded-2xl px-4 py-3 mb-3 mx-4 mt-3"
      style={{
        background: "linear-gradient(135deg, rgba(124,58,237,0.06), rgba(99,102,241,0.04))",
        border: "1px solid rgba(124,58,237,0.18)",
      }}
      data-testid="morning-briefing"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <Sunrise size={11} className="text-violet-700" strokeWidth={2.4} />
        <p className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-violet-700">
          Morning Briefing
        </p>
      </div>
      <h4
        className="text-[13px] font-semibold leading-snug mb-2 text-slate-900"
        data-testid="morning-briefing-headline"
      >
        {briefing.headline}
      </h4>
      {briefing.bullets?.length ? (
        <ul className="space-y-1">
          {briefing.bullets.slice(0, 4).map((b, i) => (
            <li
              key={i}
              className="flex items-start gap-1.5 text-[11.5px] leading-snug text-slate-700"
            >
              <span aria-hidden className="w-1 h-1 rounded-full mt-[7px] shrink-0"
                    style={{ background: "#8B5CF6" }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Focus Mode Toggle
   ─────────────────────────────────────────────────────────────────── */
const FocusModeToggle = ({ active, focusState, onToggle }) => {
  const wouldSuppress = focusState?.would_suppress_visible || 0;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="mx-4 mb-3 px-3.5 py-2 w-[calc(100%-2rem)] rounded-xl text-left transition-all flex items-center justify-between gap-3"
      style={{
        background: active ? "rgba(99,102,241,0.10)" : "rgba(148,163,184,0.06)",
        border: active
          ? "1px solid rgba(99,102,241,0.22)"
          : "1px solid rgba(148,163,184,0.18)",
      }}
      data-testid="focus-mode-toggle"
    >
      <div className="flex items-center gap-2 min-w-0">
        {active ? (
          <EyeOff size={12} className="text-indigo-700 shrink-0" strokeWidth={2.4} />
        ) : (
          <Eye size={12} className="text-slate-500 shrink-0" strokeWidth={2.4} />
        )}
        <div className="min-w-0">
          <p className="text-[11.5px] font-semibold leading-snug text-slate-900">
            {active ? "Focus Mode · Active" : "Focus Mode"}
          </p>
          <p className="text-[10.5px] text-slate-500 leading-snug">
            {active
              ? "Only critical and intervention signals visible."
              : wouldSuppress > 0
              ? `Toggling will suppress ${wouldSuppress} non-critical signal${wouldSuppress !== 1 ? "s" : ""}.`
              : "All operational signals visible."}
          </p>
        </div>
      </div>
      <span
        className="inline-block w-9 h-5 rounded-full relative shrink-0"
        style={{
          background: active ? "#7C3AED" : "rgba(148,163,184,0.30)",
          transition: "background 200ms",
        }}
      >
        <span
          className="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-all"
          style={{ left: active ? "20px" : "2px" }}
        />
      </span>
    </button>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Grouped operational alerts
   ─────────────────────────────────────────────────────────────────── */
const SignalGroup = ({ group, onAction, onOpenDeal }) => {
  const tone = TIER[group.tier] || TIER.informational;
  const Icon = tone.icon;
  const [expanded, setExpanded] = useState(false);
  const evidence = group.evidence || [];
  const canExpand = evidence.length > 1;
  return (
    <div
      className="mx-4 mb-2 rounded-xl p-3 transition-all"
      style={{
        background: tone.bg,
        borderLeft: `2px solid ${tone.dot}`,
      }}
      data-testid={`signal-group-${group.key}`}
    >
      <div className="flex items-start gap-2">
        <div
          className="w-7 h-7 rounded-lg shrink-0 inline-flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.6)", color: tone.fg }}
        >
          <Icon size={12} strokeWidth={2.3} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="text-[9.5px] uppercase tracking-[0.14em] font-bold" style={{ color: tone.fg }}>
              {tone.label}
            </p>
            {group.count > 1 ? (
              <span className="text-[10px] font-semibold text-slate-500 tabular-nums">
                · {group.count}
              </span>
            ) : null}
          </div>
          <p className="text-[12.5px] font-semibold leading-snug text-slate-900 truncate">
            {group.headline}
          </p>
          <p className="text-[11px] text-slate-600 leading-snug mt-0.5">
            {group.summary}
          </p>
          {expanded && evidence.length ? (
            <ul className="mt-2 space-y-1 pl-1 border-l border-slate-200/70">
              {evidence.map((e, i) => (
                <li
                  key={`${group.key}-${i}`}
                  onClick={() => e.deal_id && onOpenDeal?.(e.deal_id)}
                  className="pl-2 text-[10.5px] text-slate-700 truncate cursor-pointer hover:text-slate-900"
                  title={e.title}
                >
                  {e.deal_name ? <strong>{e.deal_name}</strong> : null}
                  {e.deal_name && e.title ? " · " : null}
                  {e.title || "Operational update"}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex items-center gap-2 mt-2">
            {group.primary_action && group.deal_ids?.[0] ? (
              <button
                type="button"
                onClick={() => onAction?.(group.deal_ids[0], group.primary_action.key)}
                className="inline-flex items-center gap-1 px-2 h-[24px] rounded-md text-[10.5px] font-semibold"
                style={{
                  background: "linear-gradient(135deg, rgba(99,102,241,0.10), rgba(139,92,246,0.10))",
                  color: "#4338CA",
                  border: "1px solid rgba(99,102,241,0.22)",
                }}
                data-testid={`signal-action-${group.key}`}
              >
                {group.primary_action.label}
                <ChevronRight size={10} strokeWidth={2.4} />
              </button>
            ) : null}
            {canExpand ? (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-[10.5px] font-semibold text-slate-500 hover:text-slate-700 transition-colors"
                data-testid={`signal-expand-${group.key}`}
              >
                {expanded ? "Collapse" : `View ${evidence.length} updates`}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Root panel
   ─────────────────────────────────────────────────────────────────── */
export const SignalIntelligencePanel = ({ onClose }) => {
  const nav = useNavigate();
  const [focusMode, setFocusMode] = useState(false);
  const [payload, setPayload] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(
          `/api/notifications/intelligence?focus_mode=${focusMode}`,
        );
        if (alive) setPayload(data?.signal_intelligence || null);
      } catch {
        if (alive) setPayload(null);
      }
    })();
    return () => { alive = false; };
  }, [focusMode, refreshTick]);

  const handleAction = useCallback(async (dealId, actionKey) => {
    if (!dealId || !actionKey) return;
    try {
      const { data } = await api.post(`/api/deals/${dealId}/actions/${actionKey}`, {});
      toast.success(data?.confirmation || "Action recorded on the timeline.");
      setRefreshTick((n) => n + 1);
    } catch (err) {
      const msg = err?.response?.data?.detail?.message
                || err?.response?.data?.detail
                || "Could not trigger action.";
      toast.error(typeof msg === "string" ? msg : "Could not trigger action.");
    }
  }, []);

  const handleOpenDeal = useCallback((dealId) => {
    if (!dealId) return;
    nav(`/deal-rooms/${dealId}`);
    onClose?.();
  }, [nav, onClose]);

  const groups = useMemo(() => payload?.panel?.groups || [], [payload]);
  const briefing = payload?.morning_briefing;
  const focusState = payload?.focus_mode_state;

  if (!payload) return null;

  return (
    <div data-testid="signal-intelligence-panel">
      <MorningBriefing briefing={briefing} />
      <FocusModeToggle
        active={focusMode}
        focusState={focusState}
        onToggle={() => setFocusMode((v) => !v)}
      />
      {groups.length > 0 ? (
        <div className="pb-2" data-testid="signal-groups">
          <div className="flex items-center gap-2 px-4 pt-1 pb-2">
            <p className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-slate-500">
              Operational Signals · {payload.panel.total_visible}
            </p>
            {payload.panel.total_suppressed > 0 ? (
              <p className="text-[9.5px] text-slate-400">
                · {payload.panel.total_suppressed} silent logged
              </p>
            ) : null}
          </div>
          {groups.map((g) => (
            <SignalGroup
              key={g.key}
              group={g}
              onAction={handleAction}
              onOpenDeal={handleOpenDeal}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default SignalIntelligencePanel;
